import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { StaffUserInvitation } from '@account-users/account/models/UserInvitation';
import { ISearchUser } from '@account-users/user/models/ISearchUser';
import { AccountUserType } from '@account-users/account/models/AccountUserType';
import { UserService } from '@account-users/user/services/user.service';
import { UserRoles } from '@account-users/account/models/UserRoles';
import {
  catchError,
  tap,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { FormState } from '@shared';
import { IAccountUser } from '@account-users/account/models/IAccountUser';
import { FormGroup, FormControl } from '@angular/forms';
import * as moment from 'moment';

@Component({
  selector: 'app-invite-support-user-modal',
  templateUrl: './invite-support-user-modal.component.html',
  styleUrls: ['./invite-support-user-modal.component.scss'],
})
export class InviteSupportUserModalComponent implements OnInit {
  @Output() inviteUser = new EventEmitter<StaffUserInvitation>();

  partnerInvite = false;
  accountId = '';
  accountUsers: IAccountUser[] = [];
  UserRoles = UserRoles;
  expireTimeOptions = [];

  state: FormState;
  copyUser = null;
  searching = false;
  searchFailed = false;
  userToInvite: StaffUserInvitation = {
    email: null,
    accountUserType: null,
    accountId: this.accountId,
    roles: [],
    toolsPermissions: [],
    sections: [],
  };

  selectDays = null;

  form: FormGroup = new FormGroup({
    expire: new FormControl(8),
  });

  formStates = FormState;

  usersSearch$ = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap((term) =>
        this.userService
          .searchUsers(term, this.partnerInvite ? [] : ['Support'])
          .pipe(
            map((result) => {
              this.searchFailed = false;
              return result['users'].filter(
                (user) =>
                  !this.accountUsers.some((u) => u.email === user.email),
              );
            }),
            catchError(() => {
              this.searchFailed = true;
              return of([]);
            }),
          ),
      ),
      tap(() => (this.searching = false)),
    );

  constructor(
    public activeModal: NgbActiveModal,
    public userService: UserService,
  ) {}

  ngOnInit() {
    if (this.partnerInvite) {
      this.userToInvite.roles = [UserRoles.Admin, UserRoles.User];
      this.copyUser = UserRoles.Admin;
    }
  }

  set defaultExpire(value: number) {
    this.form.get('expire').patchValue(value);
  }

  get selectedExpire() {
    return this.form.get('expire') as FormControl;
  }

  get expireString() {
    return this.expireTimeOptions.find(
      (option) => option.value === +this.selectedExpire.value,
    ).text;
  }

  get disableSubmit() {
    return (
      this.state === FormState.submitting ||
      !this.userToInvite ||
      !this.userToInvite.userId ||
      !this.copyUser
    );
  }

  get submitToolTip() {
    return this.disableSubmit
      ? this.partnerInvite
        ? 'Select a user to invite'
        : 'Select permissions for staff user'
      : '';
  }

  reset() {
    this.userToInvite.name = '';
    this.userToInvite.email = '';
    this.userToInvite.userId = '';
  }

  selectItem(user: ISearchUser) {
    if (user) {
      this.userToInvite.name = user.name;
      this.userToInvite.email = user.email;
      this.userToInvite.userId = user.userId;
      this.userToInvite.accountUserType = AccountUserType.Staff;
      this.userToInvite.accountId = this.accountId;
    }
  }

  selectPermissions(userOrAdmin) {
    this.userToInvite.toolsPermissions = [];
    this.userToInvite.sections = [];
    if (userOrAdmin === UserRoles.Admin) {
      this.userToInvite.roles = [UserRoles.Admin, UserRoles.User];
      this.copyUser = UserRoles.Admin;
    } else {
      this.userToInvite.roles = userOrAdmin.roles;
      this.userToInvite.toolsPermissions = userOrAdmin.toolPermissions;
      this.userToInvite.sections = userOrAdmin.sections;
      this.copyUser = userOrAdmin.displayName;
    }
  }

  onConfirm() {
    this.userToInvite.expires = moment()
      .add(this.selectedExpire.value, 'hours')
      .format('YYYY-MM-DDTHH:mm:ssZ');
    this.inviteUser.emit(this.userToInvite);
  }
}
